import { useNavigate } from "react-router";
import type { MessageView } from "../api/types";
import { useCharacter } from "../character";
import { useMessagePlace } from "../messagePlace";
import { source, text } from "../text";
import { ChoiceCard } from "./ChoiceCard";
import { List, ListRow } from "./List";
import { MessageList, useQuickAction } from "./MessageList";
import { Sheet } from "./Sheet";

/**
 * What the badges of a character count, spelled out: its errors and its required choices still open. In creation mode each leads to
 * its assistant step; a choice without a step is answered right here. The other hints follow, collapsed.
 */
export function OpenPointsSheet(props: { onClose: () => void }) {
  const { character, system, apply, language } = useCharacter();
  const navigate = useNavigate();
  const steps = new Map((system?.wizardSteps ?? []).map((s) => [s.id, s]));
  const stepOf = (id: string | null | undefined) => (character.mode === "creation" && id ? steps.get(id) : undefined);
  const go = (step: string) => {
    props.onClose();
    void navigate(`/c/${character.id}/assistent/${step}`);
  };
  const placeOf = useMessagePlace();
  const runAction = useQuickAction();
  const messages = character.evaluation.messages;
  const errors = messages.filter((m) => m.message.severity === "error");
  const hints = messages.filter((m) => m.message.severity !== "error");
  const choices = character.evaluation.openChoices.filter((c) => c.required);
  const linked = choices.filter((c) => stepOf(c.step));
  const answered = choices.filter((c) => !stepOf(c.step));
  const handlers = {
    onAcknowledge: (m: MessageView) => void apply({ type: "core.acknowledge", payload: { key: m.key } }),
    onUnacknowledge: (m: MessageView) => void apply({ type: "core.unacknowledge", payload: { key: m.key } }),
    onAction: runAction,
    onNavigate: (m: MessageView) => {
      const place = placeOf(m);
      if (place) {
        props.onClose();
        void navigate(`/c/${character.id}/${place}`);
      }
    },
    canNavigate: (m: MessageView) => !!placeOf(m),
  };

  return (
    <Sheet title="Fehler und offene Punkte" onClose={props.onClose} wide>
      <div className="stack open-points">
        {errors.length === 0 && choices.length === 0 && <p className="muted">Keine Fehler und keine offenen Pflichtwahlen.</p>}
        {errors.length > 0 && (
          <section aria-label="Fehler">
            <h3 className="section-title">{errors.length === 1 ? "1 Fehler" : `${errors.length} Fehler`}</h3>
            <MessageList messages={errors} {...handlers} />
          </section>
        )}
        {choices.length > 0 && (
          <section className="stack small-gap" aria-label="Offene Pflichtwahlen">
            <h3 className="section-title">{choices.length === 1 ? "1 offene Pflichtwahl" : `${choices.length} offene Pflichtwahlen`}</h3>
            {linked.length > 0 && (
              <List>
                {linked.map((c) => (
                  <ListRow
                    key={c.id}
                    title={text(c.label, language)}
                    subtitle={
                      <>
                        Schritt {text(stepOf(c.step)!.label, language)}
                        {c.source && <span className="source"> · {source(c.source)}</span>}
                      </>
                    }
                    actions={
                      <button type="button" className="link" onClick={() => go(c.step!)}>
                        zur Stelle
                      </button>
                    }
                  />
                ))}
              </List>
            )}
            {answered.map((c) => (
              <ChoiceCard key={c.id} choice={c} />
            ))}
          </section>
        )}
        {hints.length > 0 && (
          <details>
            <summary>Weitere Hinweise ({hints.length})</summary>
            <MessageList messages={hints} compact {...handlers} />
          </details>
        )}
      </div>
    </Sheet>
  );
}
